<template>
  <b-card class="m-2" no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label class="text-capitalize">{{ $t("table.show") }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block mx-50"
          />
          <label class="text-capitalize">{{ $t("table.entries") }}</label>
        </b-col>

        <b-col cols="12" md="6">
          <!-- <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search') + '...'"
              />
            </div> -->
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="refListTable"
      :items="serviceOrders"
      :fields="tableColumns"
      :per-page="perPage"
      :current-page="currentPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="isSortDirDesc"
      responsive
      primary-key="id"
      show-empty
      :empty-text="$t('no_record_found')"
    >
      <template #head()="data">
        <span> {{ $tc(data.label, 1) }}</span>
      </template>

      <template #head(actions)="data">
        <span> {{ $tc(data.label, 2) }}</span>
      </template>

      <template #head(plate)="data">
        <span> {{ $tc(data.label, 2) }}</span>
      </template>

      <template #head(tenants)="data">
        <span> {{ $tc("tenants.title", 2) }}</span>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <!-- <b-dropdown-item @click="openEditStatus(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">{{
              $t("actions.edit") + " status"
            }}</span>
          </b-dropdown-item> -->

          <b-dropdown-item @click="onClickBooklet(data.item)">
            <feather-icon icon="BookOpenIcon" />
            <span class="align-middle ml-50 text-capitalize"> booklet </span>
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template #cell(status)="data">
        <div
          :id="`${data.item.id}+tooltip`"
          style="
            width: 100%;
            display: flex;
            justify-content: space-around;
            align-items: center;
          "
        >
          <div
            :style="{
              'background-color': data.item.latest_status.color,
              'border-radius': '50px',
              height: '20px',
              width: '20px',
            }"
          ></div>
          <div>
            {{ $t(`service_orders.${data.item.latest_status.status}`) }}
          </div>
        </div>
        <b-tooltip :target="`${data.item.id}+tooltip`" triggers="hover">
          {{ $t(`service_orders.${data.item.latest_status.status}`) }}
        </b-tooltip>
      </template>
    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted text-capitalize">{{
            $t("table.showing", {
              from: dataMeta.from,
              to: dataMeta.to,
              of: dataMeta.of,
            })
          }}</span>
        </b-col>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <Pagination
            :totalList="totalList"
            :currentPage="currentPage"
            @updatePagination="update"
            :perPage="perPage"
          >
          </Pagination>
        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
} from "bootstrap-vue";
import vSelect from "vue-select";
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  refs,
} from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import moment from "moment-timezone";

export default {
  components: {
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    vSelect,
    ToastificationLoadingContent,
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const serviceOrders = ref([]);
    const totalList = ref(0);
    const currentPage = ref(1);
    const perPage = ref(10);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref("");
    const sortBy = ref("id");
    const isSortDirDesc = ref(true);
    const toast = useToast();
    const refListTable = ref(null);

    const tableColumns = [
      { label: "actions.name", key: "actions" },
      {
        label: "service_orders.reference_number",
        key: "reference_number",
        sortable: false,
      },
      {
        label: "service_orders.facility",
        key: "unit.description",
        sortable: false,
      },
      {
        label: "service_orders.attention_date",
        key: "attention_date",
        sortable: true,
        formatter: "formatDateAssigned",
      },
      {
        label: "service_orders.status",
        key: "status",
        sortable: true,
      },
      {
        label: "service_orders.status_change_date",
        key: "status[0].pivot.updated_at",
        sortable: true,
        formatter: "formatDateAssigned",
      },
      {
        label: "date.created_at",
        key: "created_at",
        sortable: true,
        formatter: "formatDateAssigned",
      },
      {
        label: "date.updated_at",
        key: "updated_at",
        sortable: true,
        formatter: "formatDateAssigned",
      },
    ];
    const update = (data) => {
      currentPage.value = data;
    };

    const dataMeta = computed(() => {
      const localItemsCount = refListTable.value
        ? refListTable.value.localItems.length
        : 0;
      return {
        from:
          perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalList.value,
      };
    });

    const fetchList = () => {
      //console.log(props.filters)

      let params = {
        id_maintenance_company: props.filters.maintenanceFilter,
        id_unit: props.filters.unitFilter,
        id_status: props.filters.statusFilter,
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
      };

      if (props.filters.startDate && props.filters.endDate) {
        params = {
          ...params, 
          date_filter: "attention_date", 
          start_date: props.filters.startDate,
          end_date: props.filters.endDate,
        };
      }
      store
        .dispatch("service_orders/getAll", params)
        .then((response) => {
          serviceOrders.value = response.data;
          totalList.value = response.meta.total;
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: "Error obteniendo la información",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const onClickBooklet = (data) => {
      console.log({ data });
      let detail = {};
      detail.template_id = data.id_maintenance_template;
      detail.service_id = data.id;
      detail.status_id = data.latest_status.id;
      detail.reference_number = data.reference_number;
      emit("showBookletModal", detail);
    };

    const openEditStatus = (data) => {
      let detail = {};
      detail.service_id = data.id;
      detail.status_id = data.latest_status.id;
      detail.reference_number = data.reference_number;
      emit("showStatusModal", detail);
    };

    const getColorLastStatus = (data) => {
      let long = data.length;
      return data[long - 1];
    };

    const formatDateAssigned = (value) => {
      return moment(value).format("DD/MM/YYYY HH:mm:ss");
    };

    const refreshTable = () => {
      //console.log("refreshTable");
      fetchList();
    };

    // watch(
    //   () => props.filters,
    //   () => {
    //     if (Object.keys(props.filters).length > 0) {
    //       console.log("update filters");
    //       fetchList();
    //     }
    //   },
    //   { deep: true }
    // );

    watch(searchQuery, fetchList);

    onMounted(() => {
      fetchList();
    });

    return {
      serviceOrders,
      totalList,
      currentPage,
      perPage,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      tableColumns,
      formatDateAssigned,
      dataMeta,
      refListTable,
      update,
      onClickBooklet,
      openEditStatus,
      getColorLastStatus,
      refreshTable,
    };
  },
};
</script>
